import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Box } from "@mui/material"
import { withTranslation } from "react-i18next"
import ListDataTable from "../../components/Common/list-data-table"
import { apiBuscarConsultaVendas } from "../../helpers/backend_helper"
import { describeDateTime, describeMoney } from "../../helpers/utils"
import { tableTokens } from "../../components/Common/table-theme"

const TAMANHO_PAGINA_PADRAO = 50
const TAMANHOS_PAGINA = [50, 100]

const celulaMonetaria = valor => (
  <Box sx={ { display: "block", textAlign: "right" } } className={ (valor || 0) === 0 ? "text-light" : "" }>
    { describeMoney(valor) }
  </Box>
)

const celulaNumerica = valor => (
  <Box sx={ { display: "block", textAlign: "right" } }>{ valor || 0 }</Box>
)

/**
 * Grid da Consulta de Vendas. Paginação, filtro e ordenação são resolvidos no servidor pelo
 * ListDataTable, que refaz a busca sempre que filters muda de referência. As colunas calculadas
 * (situação, tipo, canal, totais) já vêm prontas da API.
 */
const ConsultaVendasTable = props => {
  const { filters, columns, onVisualizar, podeVisualizar, t } = props
  const [data, setData] = useState([])

  /*
   * Filtro de coluna fica desligado em dois casos: quando o filtro do topo já cobre a coluna
   * (empresa, período, situação, tipo, canal, loja, caixa, operador, vendedor e faixa de valor),
   * e nas colunas calculadas, que não têm atributo correspondente em tb_vendas — o filtro seria
   * descartado no processWhere sem o usuário perceber.
   */
  const colunas = useMemo(() => columns ||
    [
      { header: t("consultavendas.grid.data"), accessorKey: "Data", size: 150, enableColumnFilter: false,
        sortingFn: "datetime", Cell: ({ cell }) => describeDateTime(cell?.getValue()) },
      { header: t("consultavendas.grid.empresa"), accessorKey: "nomeEmpresa", size: 160, enableColumnFilter: false },
      { header: t("consultavendas.grid.loja"), accessorKey: "Loja", size: 90, enableColumnFilter: false },
      { header: t("consultavendas.grid.sequencia"), accessorKey: "Seq", size: 110 },
      { header: t("consultavendas.grid.consumidor"), accessorKey: "Cons_Nome", size: 200, filterFn: "contains" },
      { header: t("consultavendas.grid.documento"), accessorKey: "Cons_CNPJCPF", size: 150, filterFn: "contains" },
      { header: t("consultavendas.grid.situacao"), accessorKey: "status_descricao", size: 190, enableColumnFilter: false },
      { header: t("consultavendas.grid.tipo"), accessorKey: "tipo_descricao", size: 180, enableColumnFilter: false },
      { header: t("consultavendas.grid.canal"), accessorKey: "canal_descricao", size: 220, enableColumnFilter: false },
      { header: t("consultavendas.grid.quantidade.itens"), accessorKey: "quantidade_itens", size: 130,
        enableColumnFilter: false, Cell: ({ cell }) => celulaNumerica(cell?.getValue()) },
      { header: t("consultavendas.grid.total.venda"), accessorKey: "total_venda", size: 140,
        enableColumnFilter: false, Cell: ({ cell }) => celulaMonetaria(cell?.getValue()) },
      { header: t("consultavendas.grid.total.recebido"), accessorKey: "total_recebido", size: 150,
        enableColumnFilter: false, Cell: ({ cell }) => celulaMonetaria(cell?.getValue()) },
      { header: t("consultavendas.grid.nfe"), accessorKey: "NFe_Numero", size: 120 },
      { header: t("consultavendas.grid.cupom"), accessorKey: "Cupom", size: 110 },
      { header: t("consultavendas.grid.caixa"), accessorKey: "Caixa", size: 100, enableColumnFilter: false },
      { header: t("consultavendas.grid.operador"), accessorKey: "Func_Caixa", size: 120, enableColumnFilter: false },
      { header: t("consultavendas.grid.vendedor"), accessorKey: "Func_Venda", size: 120, enableColumnFilter: false },
      // idCloud e idEmpresa não são exibidos, mas identificam a venda para o detalhamento.
      { header: t("consultavendas.grid.idcloud"), accessorKey: "id", size: 100, enableColumnFilter: false },
      { header: t("consultavendas.grid.idempresa"), accessorKey: "idEmpresa", size: 110, enableColumnFilter: false }
    ], [columns, t])

  return (
    <ListDataTable
      searchFn={ params => apiBuscarConsultaVendas(params) }
      advancedFilters={ filters }
      columns={ colunas }
      data={ data }
      setData={ setData }
      initialPageSize={ TAMANHO_PAGINA_PADRAO }
      muiTablePaginationProps={ { rowsPerPageOptions: TAMANHOS_PAGINA } }
      columnVisibility={ {
        Cons_CNPJCPF: false,
        Cupom: false,
        Caixa: false,
        Func_Caixa: false,
        Func_Venda: false,
        id: false,
        idEmpresa: false
      } }
      defaultSorting={ [{ id: "Data", desc: true }, { id: "id", desc: true }] }
      enableRowActions={ Boolean(podeVisualizar) }
      renderRowActions={ row => (
        <Box sx={ { display: "flex", gap: "1rem" } }>
          <Link to="#" className="text-primary">
            <i className="mdi mdi-eye font-size-18 ms-1"
               title={ t("consultavendas.grid.visualizar") }
               onClick={ () => onVisualizar && onVisualizar(row.original) } />
          </Link>
        </Box>
      ) }
      renderEmptyRowsFallback={ () => (
        <Box sx={ {
          position: "sticky",
          left: 0,
          width: "100%",
          textAlign: "center",
          padding: "0.5rem",
          color: tableTokens.colorMuted,
          fontSize: tableTokens.fontSizeUi,
          fontFamily: tableTokens.fontFamily
        } }>
          { t("consultavendas.grid.vazio") }
        </Box>
      ) }
    />
  )
}

ConsultaVendasTable.propTypes = {
  filters: PropTypes.array,
  columns: PropTypes.array,
  onVisualizar: PropTypes.func,
  podeVisualizar: PropTypes.bool,
  t: PropTypes.func
}

export default withTranslation()(ConsultaVendasTable)
